/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Icon, ICON_SIZES, StackView, STACK_ALIGN, STACK_GAP, Text, TEXT_SIZE } from '@veeam-vspc/components';
import { BackupAgentWorkloads, ProtectedDataFilterBy } from '@veeam-vspc/models/web-controllers';
import { observer } from 'mobx-react-lite';

import type { BaseProtectedDataWorkloadOverviewBackupAgentWorkloads, ProtectedDataOverviewRequest } from '@veeam-vspc/models/web-controllers';

import { TableWidget } from 'views/components/TableWidget';
import { NameColumn } from 'views/components/TableWidget/components/NameColumn';
import successIcon from 'images/success.svg';
import serverIcon from 'images/pages/protected-data-summary/cc-server.svg';
import workstationIcon from 'images/pages/protected-data-summary/cc-workstation.svg';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useIntervalRequest } from 'views/hooks';
import { EmptyColumnPlaceholder } from 'views/components/TableWidget/components/EmptyColumnPlaceholder';
import { NoInfoColumn } from 'views/components/TableWidget/components/NoInfoColumn';
import agentNotInstalled from 'images/agent-not-installed.svg';
import { useProtectedDataSummaryStore } from '../../store';

const icons = {
    [BackupAgentWorkloads.Servers]: serverIcon,
    [BackupAgentWorkloads.Workstations]: workstationIcon,
};

export const VeeamAgents: React.FC = observer(() => {
    const store = useProtectedDataSummaryStore();
    const lang = useLang();
    const didMount = useRef(false);
    const abortController = useRef(new AbortController());

    const names = useMemo(() => ({
        [BackupAgentWorkloads.Servers]: lang.SERVERS,
        [BackupAgentWorkloads.Workstations]: lang.WORKSTATIONS,
    }), []);

    const request = useIntervalRequest<ProtectedDataOverviewRequest, BaseProtectedDataWorkloadOverviewBackupAgentWorkloads[]>(
        '/ProtectedOverview/BackupAgents',
        {
            rpoDays: store.rpoDays,
            filterType: ProtectedDataFilterBy.Rpo,
        },
        {
            signal: abortController.current.signal,
        });

    useEffect(() => {
        if (didMount.current) {
            abortController.current.abort();

            abortController.current = new AbortController();
            return request.forceRequest();
        }

        didMount.current = true;
    }, [store.rpoDays]);

    const items = request.isError || !Array.isArray(request.data)
        ? []
        : (request.data as BaseProtectedDataWorkloadOverviewBackupAgentWorkloads[]);

    return (
        <TableWidget
            data={items}
            columns={[
                {
                    name: lang.VEEAM_AGENTS,
                    cellRenderer: ({ rowData, key }) => (
                        <NameColumn
                            icon={icons[rowData.workloadType]}
                            name={names[rowData.workloadType]}
                            key={key}
                        />
                    ),
                },
                {
                    cellRenderer: ({ key }) => <EmptyColumnPlaceholder key={key} />,
                },
                {
                    cellRenderer: ({ rowData, key }) => {
                        const protectedCount = rowData.protectionData.reduce((acc, item) => acc + item.protectedCount.value, 0);
                        const overallCount = rowData.protectionData.reduce((acc, item) => acc + item.overallCount, 0);

                        if (overallCount === 0) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.center}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <NoInfoColumn />
                                </StackView>
                            );
                        }

                        if (protectedCount !== overallCount) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.end}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <Text size={TEXT_SIZE.xl}>{protectedCount}</Text>
                                    <Text size={TEXT_SIZE.l}>of</Text>
                                    <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                                </StackView>
                            );
                        }

                        return (
                            <StackView
                                align={STACK_ALIGN.center}
                                gap={STACK_GAP.s}
                                key={key}
                            >
                                <Icon src={successIcon} size={ICON_SIZES.xl} />
                                <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                            </StackView>
                        );
                    },
                },
            ]}
            loading={request.loading}
            noDataConfig={{
                linkText: lang.START_MANAGING_VEEAM_AGENT,
                icon: agentNotInstalled,
                iconSize: ICON_SIZES.xl,
                linkHandler: () => window.location.assign('#home/discovery/tab/discoveredComputersTab/s/discoveredComputers'),
            }}
        />
    );
});
